<script>
  import { _, locale } from "./i18n.js";
  import {
    f7,
    Page,
    Navbar,
    NavRight,
    Preloader,
    Toolbar,
    BlockTitle,
    Link,
    List,
    ListItem,
    Icon,
    Sheet,
    PageContent,
    Button
  } from "framework7-svelte";
  import { onDestroy } from "svelte";
  import {
    getSecuredFirestore,
    getStorage,
    getCachedFirst
  } from "./Firestore.js";
  import { getStaticDeliveryLocationImage } from "./AddressService.js";
  import { getProfile } from "./UserService.js";
  import moment from "moment";
  import SignaturePad from "signature_pad";

  let snapshotListener;
  onDestroy(async () => {
    snapshotListener && snapshotListener();
    f7.sheet.close();
  });

  export let f7router;
  export let id;

  let orderDoc;
  let order;
  let profilePhoto;
  let delivery;
  let orderStatus;
  let paymentStatus;
  let paymentMethod;
  let customerSignature;

  let admin;
  getProfile().then(function(profile) {
    admin = profile.admin === true;
  });

  function updatePaymentStatus(status) {
    paymentStatus = status;
    orderDoc.update({ paymentStatus: paymentStatus });
  }

  function updatePaymentMethod(method) {
    paymentMethod = method;
    orderDoc.update({ paymentMethod: paymentMethod });
  }

  function updateOrderStatus(status) {
    orderStatus = status;
    orderDoc.update({ status: orderStatus });
  }

  function getProfilePhoto(order) {
    return getCachedFirst(order.get("userRef")).then(function(user) {
      return user.get("profilePhoto");
    });
  }

  getSecuredFirestore().then(function(firestore) {
    orderDoc = firestore.collection("orders").doc(id);
    snapshotListener = orderDoc.onSnapshot(async function(o) {
      if (!o.exists) {
        f7.dialog.alert(
          $_("orderDetail.orderNotFound", {
            values: {
              id: id
            }
          }) + "!",
          function() {
            f7router.navigate("/Order/My");
          }
        );
      }

      order = o;
      profilePhoto = getProfilePhoto(o);
      delivery = o.get("delivery");
      orderStatus = o.get("status") || "Pending";
      paymentStatus = o.get("paymentStatus") || "Outstanding";
      paymentMethod = o.get("paymentMethod") || "cash";
      customerSignature = delivery.customerSignature;
    });
  });

  function getOrderStatusLabel(status) {
    switch (status) {
      case "Pending":
        return $_("order.pending");
      case "Delivered":
        return $_("order.delivered");
    }
  }

  function getPaymentMethodLabel(paymentMethod) {
    switch (paymentMethod) {
      case "cash":
        return $_("orderDetail.payment.cash");
      case "bankTransfer":
        return $_("orderDetail.payment.bankAccount");
      case "promptPay":
        return $_("orderDetail.payment.promptPay");
    }
  }

  function getPaymentStatusLabel(paymentStatus) {
    switch (paymentStatus) {
      case "Outstanding":
        return $_("orderDetail.outstanding");
      case "Paid":
        return $_("orderDetail.paid");
    }
  }

  function renderIcon(value) {
    switch (value) {
      case "Delivered":
        return '<i style="color: green; margin-right: 6px;" class="icon icon-deliverable" />';
      case "Paid":
        return '<i style="color: green; margin-right: 6px;" class="icon icon-check" />';
      case "Pending":
        return '<i style="color: red; margin-right: 6px;" class="icon icon-pending" />';
      case "Outstanding":
        return '<i style="color: red; margin-right: 6px;" class="icon icon-warning" />';
      case "bankTransfer":
        return '<i style="color: rgb(66,18,116); margin-right: 6px;" class="icon icon-scb" />';
      case "promptPay":
        return '<i style="margin-right: 6px;" class="icon icon-qrcode" />';
      case "cash":
        return '<i style="margin-right: 6px;" class="icon icon-cash" />';
    }
  }

  function deleteOrder() {
    f7.dialog.confirm($_("order.deleteOrder.confirm"), () => {
      let dialog = f7.dialog.preloader($_("order.deleting"));
      // remove listener to prevent error dialog
      snapshotListener();
      getSecuredFirestore().then(function(firestore) {
        firestore
          .collection("orders")
          .doc(id)
          .delete()
          .then(function() {
            dialog.close();
            f7router.back();
          })
          .catch(function(e) {
            console.log(e);
            dialog.close();

            f7.dialog.alert($_("order.delete.error"));
          });
      });
    });
  }

  function removeSignature() {
    f7.dialog.confirm($_("orderDetail.removeSignature.confirm"), () => {
      let dialog = f7.dialog.preloader($_("orderDetail.signature.removing"));

      let copy = { ...delivery };
      delete copy.customerSignature;
      let firestorePromise = orderDoc.update({ delivery: copy });

      getStorage().then(function(storage) {
        let signatureRef = storage.ref().child(id + "_signature.png");
        let storagePromise = signatureRef.delete();

        Promise.all([firestorePromise, storagePromise])
          .then(values => {
            dialog.close();
            f7.toast
              .create({
                text: $_("orderDetail.signatureRemoved"),
                position: "top",
                closeTimeout: 4000
              })
              .open();
          })
          .catch(function(e) {
            console.log(e);
            dialog.close();

            f7.dialog.alert($_("orderDetail.removeSignature.error"));
          });
      });
    });
  }

  let signaturePad;
  function openSignatureSheet(e) {
    let canvas = document.getElementById("signature");

    signaturePad = new SignaturePad(canvas, {
      minWidth: 5,
      maxWidth: 10,
      penColor: "rgb(66, 133, 244)"
    });

    function resizeCanvas() {
      var ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      signaturePad.clear(); // otherwise isEmpty() might return incorrect value
    }

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();
  }

  function confirmReceipt() {
    if (signaturePad.isEmpty()) {
      f7.toast
        .create({
          text: $_("orderDetail.missingSignature"),
          position: "top",
          closeTimeout: 4000
        })
        .open();
    } else {
      let dialog = f7.dialog.preloader(
        $_("orderDetail.confirmingReceipt") + "..."
      );

      const canvas = document.getElementById("signature");
      canvas.toBlob(function(blob) {
        getStorage().then(function(storage) {
          var storageRef = storage.ref();
          let signatureFileName = id + "_signature.png";
          var imageRef = storageRef.child(signatureFileName);
          imageRef
            .put(blob)
            .then(function(snapshot) {
              return snapshot.ref.getDownloadURL();
            })
            .then(function(url) {
              customerSignature = url;
              delivery.customerSignature = customerSignature;
              orderStatus = "Delivered";
              orderDoc.update({ delivery: delivery, status: orderStatus });

              dialog.close();
              f7.sheet.close();
              f7.toast
                .create({
                  text: $_("orderDetail.signatureProcessed"),
                  position: "top",
                  closeTimeout: 4000
                })
                .open();
            });
        });
      }, "image/png");
    }
  }

  function clearSignature() {
    signaturePad.clear();
  }
</script>

<style>
  .smartSelectLink {
    color: rgb(0, 128, 228);
  }
</style>

<Page>
  <span style="display: none" class="hidden" />
  <PageContent>
    <Navbar title={$_('orderDetail.navTitle')} backLink={$_('base.back')}>
      <NavRight>
        {#await profilePhoto then photo}
          <img
            alt=""
            style="margin-right: 5px; width: 50px; border-radius: 50%"
            src={photo} />
        {/await}
      </NavRight>
    </Navbar>

    {#if !order}
      <div
        style="align-items: center; display: flex; justify-content: center;
        flex-direction: column; height: 100%;">

        <Preloader />
      </div>
    {:else if order && order.exists}
      <BlockTitle medium>{$_('orderDetail.title')}</BlockTitle>

      <List mediaList>
        <ListItem title={$_('orderDetail.id')} after={id} />
        <ListItem
          title={$_('orderDetail.date')}
          after={moment(order
              .get('created')
              .toDate()).format('Do MMMM YYYY, H:mm A')} />
        {#if admin}
          <ListItem
            title={$_('orderDetail.status')}
            smartSelect
            smartSelectParams={{ sheetCloseLinkText: $_('base.done'), valueEl: '.hidden', openIn: 'sheet', routableModals: false, closeOnSelect: true, on: { change: function(e) {
                  let orderStatus = e.selectEl.selectedOptions[0].value;
                  updateOrderStatus(orderStatus);
                } } }}>
            <div class="item-after" slot="after-title">
              {@html renderIcon(orderStatus)}
              <span class="smartSelectLink">
                {getOrderStatusLabel(orderStatus)}
              </span>
            </div>
            <select name="orderStatus" value={orderStatus}>
              <option value="Pending">{$_('order.pending')}</option>
              <option value="Delivered">{$_('order.delivered')}</option>
            </select>
          </ListItem>
        {:else}
          <ListItem title={$_('orderDetail.status')}>
            <div class="item-after" slot="after-title">
              {@html renderIcon(orderStatus)}
              {getOrderStatusLabel(orderStatus)}
            </div>
          </ListItem>
        {/if}

        <ListItem title={$_('orderDetail.name')} after={order.get('name')} />
        <ListItem title={$_('orderDetail.address')}>
          <div style="margin-top: 10px" class="text item-text">
            {order.get('address')}
          </div>
        </ListItem>
      </List>

      <BlockTitle medium>{$_('orderDetail.items')}</BlockTitle>
      <List>
        {#each order.get('items') as item}
          <ListItem
            title="{item.amount + 'x '}{item.product.title}"
            after="{(item.amount * item.product.price).toLocaleString($locale, {
              minimumFractionDigits: 2
            })} THB" />
        {/each}

        <ListItem
          style="font-weight: bold !important"
          title={$_('orderDetail.totalAmount')}
          after={order
            .get('totalAmount')
            .toLocaleString($locale, {
              minimumFractionDigits: 2
            }) + ' THB'} />
      </List>

      <BlockTitle medium>{$_('orderDetail.payment')}</BlockTitle>
      <List>
        {#if admin}
          <ListItem
            title={$_('orderDetail.paymentMethod')}
            smartSelect
            smartSelectParams={{ sheetCloseLinkText: $_('base.done'), valueEl: '.hidden', openIn: 'sheet', routableModals: false, closeOnSelect: true, on: { change: function(e) {
                  let paymentMethod = e.selectEl.selectedOptions[0].value;
                  updatePaymentMethod(paymentMethod);
                } } }}>
            <div class="item-after" slot="after-title">
              {@html renderIcon(paymentMethod)}
              <span class="smartSelectLink">
                {getPaymentMethodLabel(paymentMethod)}
              </span>
            </div>
            <select name="paymentMethod" value={paymentMethod}>
              <option value="cash">{getPaymentMethodLabel('cash')}</option>
              <option value="bankTransfer">
                {getPaymentMethodLabel('bankTransfer')}
              </option>
              <option value="promptPay">
                {getPaymentMethodLabel('promptPay')}
              </option>
            </select>
          </ListItem>
        {:else}
          <ListItem title={$_('orderDetail.paymentMethod')}>
            <div class="item-after" slot="after-title">
              {@html renderIcon(paymentMethod)}
              {getPaymentMethodLabel(paymentMethod)}
            </div>
          </ListItem>
        {/if}

        {#if admin}
          <ListItem
            title={$_('orderDetail.status')}
            smartSelect
            smartSelectParams={{ sheetCloseLinkText: $_('base.done'), valueEl: '.hidden', openIn: 'sheet', routableModals: false, closeOnSelect: true, on: { change: function(e) {
                  let paymentStatus = e.selectEl.selectedOptions[0].value;
                  updatePaymentStatus(paymentStatus);
                } } }}>
            <div class="item-after" slot="after-title">
              {@html renderIcon(paymentStatus)}
              <span class="smartSelectLink">
                {getPaymentStatusLabel(paymentStatus)}
              </span>
            </div>
            <select name="paymentStatus" value={paymentStatus}>
              <option value="Outstanding">
                {$_('orderDetail.outstanding')}
              </option>
              <option value="Paid">{$_('orderDetail.paid')}</option>
            </select>
          </ListItem>
        {:else}
          <ListItem title={$_('orderDetail.status')}>
            <div class="item-after" slot="after-title">
              {@html renderIcon(paymentStatus)}
              {getPaymentStatusLabel(paymentStatus)}
            </div>
          </ListItem>
        {/if}

        {#if order.get('paymentSlipUrl')}
          <ListItem title={$_('orderDetail.paymentSlip')}>
            <div slot="after-title">
              <img
                style="max-height: 200px; margin-top: 10px; margin-bottom: 10px"
                alt="Payment Slip"
                src={order.get('paymentSlipUrl')} />
            </div>
          </ListItem>
        {/if}
      </List>

      {#if delivery}
        <BlockTitle medium>{$_('orderDetail.delivery')}</BlockTitle>
        <List mediaList>
          {#if delivery.date}
            <ListItem
              title={$_('orderDetail.deliveryDate')}
              after={moment(delivery.date.toDate()).format('Do MMMM YYYY')} />
          {/if}

          {#if delivery.time}
            <ListItem
              title={$_('orderDetail.deliveryTime')}
              after={delivery.time} />
          {/if}

          {#if delivery.price}
            <ListItem
              title={$_('orderDetail.deliveryPrice')}
              after="{delivery.price.toLocaleString($locale, {
                minimumFractionDigits: 2
              })} THB" />
          {/if}

          {#if customerSignature}
            <ListItem title={$_('orderDetail.customerSignature')}>
              <div slot="after-title">
                <img
                  style="max-height: 200px; margin-top: 10px; margin-bottom:
                  10px"
                  alt="Customer Signature"
                  src={customerSignature} />
              </div>
            </ListItem>
          {/if}

          {#if delivery.location}
            <ListItem title={$_('orderDetail.location')}>
              <div class="text">
                <Link
                  target="_new"
                  external
                  href="https://www.google.com/maps/search/{delivery.location.latitude},{delivery.location.longitude}">
                  <img
                    style="margin-top: 10px; width: 100%"
                    alt=""
                    src={getStaticDeliveryLocationImage({
                      lat: delivery.location.latitude,
                      lng: delivery.location.longitude
                    })} />
                </Link>
              </div>
            </ListItem>
          {/if}
        </List>
      {/if}
    {/if}
  </PageContent>

  {#if admin}
    <Toolbar tabbar labels position={'bottom'}>
      {#if !customerSignature}
        <Link
          actionsOpen=".signature-sheet"
          text={$_('orderDetail.signByCustomer')}
          style="color: rgb(0, 122, 255)"
          icon="icon icon-signature" />
      {:else}
        <Link
          on:click={removeSignature}
          text={$_('orderDetail.removeSignature')}
          style="color: rgb(0, 122, 255)"
          icon="icon icon-bin" />
      {/if}
      <Link
        hidden={!admin}
        on:click={deleteOrder}
        style="color: red"
        icon="icon icon-bin"
        text={$_('orderDetail.deleteOrder')} />
    </Toolbar>
  {/if}

  <Sheet
    class="signature-sheet"
    style="height: 100%; --f7-sheet-bg-color: #fff"
    push={true}
    onSheetOpen={openSignatureSheet}>
    <Toolbar>
      <div class="left" />
      <div class="right">
        <Link sheetClose>{$_('base.close')}</Link>
      </div>
    </Toolbar>
    <PageContent>
      <canvas
        style="width: 100%; height: 100%; position: absolute"
        id="signature" />
      <div
        style="position: absolute; bottom: 20px; right: 0%;"
        class="padding-horizontal padding-bottom">
        <Button color="green" fill round large on:click={confirmReceipt}>
          {$_('orderDetail.confirmReceipt')}
        </Button>
      </div>
      <div
        style="position: absolute; bottom: 20px; left: 0;"
        class="padding-horizontal padding-bottom">
        <Button fill round large on:click={clearSignature}>
          {$_('orderDetail.clearSignature')}
        </Button>
      </div>
    </PageContent>
  </Sheet>
</Page>
